<div class="terminal-health">
  <p class="text-muted mb-3">
    Comms failure rate per payment terminal — compare siblings at the same store to spot a bad physical unit.
    Default view is the <strong>last 30 days</strong> (monthly ops overview).
    <strong class="text-danger">New regression</strong> = rate jumped vs the prior equal-length window;
    <strong class="text-danger">New terminal — high error rate</strong> = first seen within this window and already failing badly;
    <strong class="text-warning">Elevated</strong> = failure rate ≥{{ reassuringRateFloorLabel }} with a flat or inconclusive trend — not healthy;
    <strong>Chronic</strong> = elevated across the whole range (only shown when the window is long enough to tell).
    Terminals above {{ reassuringRateFloorLabel }} never show a reassuring Stable/Improving badge alone.
  </p>

  <nb-card class="mb-4">
    <nb-card-body>
      <div class="row align-items-end g-3">
        <div class="col-md-3">
          <label class="form-label">From</label>
          <input type="date" class="form-control" [(ngModel)]="dateFrom" (change)="onFilterChange()">
        </div>
        <div class="col-md-3">
          <label class="form-label">To</label>
          <input type="date" class="form-control" [(ngModel)]="dateTo" (change)="onFilterChange()">
        </div>
        <div class="col-md-2">
          <button class="btn btn-outline-secondary w-100" (click)="resetFilters()">Last 30 days</button>
        </div>
        <div class="col-md-2">
          <button class="btn btn-primary w-100" (click)="loadHealth()">
            <nb-icon icon="refresh-outline"></nb-icon>
            Refresh
          </button>
        </div>
      </div>
    </nb-card-body>
  </nb-card>

  <div *ngIf="endpointUnavailable" class="alert alert-info mb-4">
    <strong>Backend not ready</strong> — {{ errorMessage }}
  </div>

  <div *ngIf="isLoading" class="text-center p-5">
    <nb-spinner size="large"></nb-spinner>
    <p class="mt-2 text-muted">Loading terminal health…</p>
  </div>

  <div *ngIf="!isLoading && errorMessage && !endpointUnavailable" class="text-center p-4">
    <p class="text-danger">{{ errorMessage }}</p>
    <button class="btn btn-outline-secondary btn-sm" (click)="loadHealth()">Try again</button>
  </div>

  <div *ngIf="!isLoading && !errorMessage && terminals.length === 0" class="text-center p-4">
    <h5>No terminal health data</h5>
    <p class="text-muted">No comms failure stats for this store in the selected range.</p>
  </div>

  <nb-card class="mb-4" *ngIf="!isLoading && terminals.length > 1">
    <nb-card-header>Comms failure rate comparison</nb-card-header>
    <nb-card-body>
      <div class="comparison-bars">
        <div *ngFor="let row of terminals; trackBy: trackByTerminalId" class="comparison-row">
          <div class="comparison-label font-monospace">
            {{ row.terminalName || row.terminalId }}
            <span *ngIf="isNewRegression(row)" class="badge regression-badge ms-1">New regression</span>
            <span *ngIf="isNewTerminalHighRate(row)" class="badge new-terminal-high-badge ms-1">New terminal — high error rate</span>
            <span *ngIf="shouldShowChronicBadge(row)" class="badge chronic-badge ms-1">Chronic</span>
            <span *ngIf="isImprovingStillHigh(row)" class="badge improving-still-high-badge ms-1">{{ getImprovingStillHighLabel(row) }}</span>
            <span *ngIf="isElevatedAbsolute(row)" class="badge elevated-absolute-badge ms-1">{{ getElevatedAbsoluteLabel(row) }}</span>
            <span *ngIf="isImproving(row)" class="badge improving-badge ms-1">Improving</span>
          </div>
          <div class="comparison-bar-track">
            <div class="comparison-bar-fill" [ngClass]="getRateBarClass(row.commsFailureRate)"
              [style.width.%]="Math.min(row.commsFailureRate * 100, 100)">
            </div>
          </div>
          <div class="comparison-value">
            <span class="badge" [ngClass]="getRateBadgeClass(row.commsFailureRate)">
              {{ formatRate(row.commsFailureRate) }}
            </span>
          </div>
        </div>
      </div>
    </nb-card-body>
  </nb-card>

  <nb-card *ngIf="!isLoading && terminals.length > 0">
    <nb-card-body class="p-0">
      <div class="table-responsive">
        <table class="table table-hover mb-0">
          <thead class="table-light">
            <tr>
              <th>Terminal</th>
              <th>Comms failure</th>
              <th>Error rate</th>
              <th>Attempts</th>
              <th>Comms failures</th>
              <th>Trend</th>
              <th>Signal</th>
              <th>Δ vs prior</th>
            </tr>
          </thead>
          <tbody>
            <tr *ngFor="let row of terminals; trackBy: trackByTerminalId"
              [class.new-regression-row]="isNewRegression(row)"
              [class.new-terminal-high-row]="isNewTerminalHighRate(row)"
              [class.worst-row]="row.isWorst && !isNewRegression(row) && !isNewTerminalHighRate(row) && row.commsFailureRate >= 0.25"
              [class.elevated-absolute-row]="isElevatedAbsolute(row)"
              [class.improving-still-high-row]="isImprovingStillHigh(row)"
              [class.improving-row]="isImproving(row)">
              <td>
                <strong class="font-monospace">{{ row.terminalId }}</strong>
                <div class="text-muted small" *ngIf="row.terminalName">{{ row.terminalName }}</div>
              </td>
              <td>
                <span class="badge rate-badge" [ngClass]="getRateBadgeClass(row.commsFailureRate)">
                  {{ formatRate(row.commsFailureRate) }}
                </span>
              </td>
              <td class="small text-muted">{{ formatRate(row.errorRate) }}</td>
              <td>{{ row.totalAttempts ?? '—' }}</td>
              <td>{{ row.commsFailures ?? '—' }}</td>
              <td class="sparkline-cell">
                <div class="sparkline-wrap" *ngIf="row.trend.length > 0 && row.sparklineData">
                  <canvas baseChart
                    [type]="'line'"
                    [data]="row.sparklineData"
                    [options]="row.sparklineOptions">
                  </canvas>
                </div>
                <span *ngIf="!row.trend.length" class="text-muted small">No trend data</span>
              </td>
              <td>
                <span *ngIf="isNewRegression(row)" class="badge regression-badge"
                  nbTooltip="Comms failure rate rose vs the preceding equal-length window — likely a new fault">
                  New regression
                </span>
                <span *ngIf="isNewTerminalHighRate(row)" class="badge new-terminal-high-badge"
                  [nbTooltip]="getNewTerminalHighRateTooltip(row)">
                  New terminal — high error rate
                </span>
                <span *ngIf="shouldShowChronicBadge(row)" class="badge chronic-badge"
                  [nbTooltip]="getChronicTooltip(row)">
                  Chronic
                </span>
                <span *ngIf="isImprovingStillHigh(row)" class="badge improving-still-high-badge"
                  [nbTooltip]="getImprovingStillHighTooltip(row)">
                  {{ getImprovingStillHighLabel(row) }}
                </span>
                <span *ngIf="isElevatedAbsolute(row)" class="badge elevated-absolute-badge"
                  [nbTooltip]="getElevatedAbsoluteTooltip(row)">
                  {{ getElevatedAbsoluteLabel(row) }}
                </span>
                <span *ngIf="isImproving(row)" class="badge improving-badge"
                  nbTooltip="Comms failure rate dropped vs the preceding window">
                  Improving
                </span>
                <span *ngIf="isQuietInsufficientHistory(row)" class="text-muted small"
                  [nbTooltip]="getInsufficientHistoryTooltip(row)">
                  Not enough prior history
                </span>
                <span *ngIf="isStable(row)" class="text-muted small">Stable</span>
              </td>
              <td>
                <span *ngIf="hasDeltaComparison(row) && row.deltaFromPriorPeriod !== null"
                  [class.text-danger]="isNewRegression(row)"
                  [class.text-success]="isImproving(row)"
                  [class.text-warning]="isImprovingStillHigh(row) || isElevatedAbsolute(row)"
                  [class.text-muted]="isStable(row)"
                  class="small fw-semibold">
                  {{ formatDelta(row.deltaFromPriorPeriod) }}
                </span>
                <span *ngIf="hasInsufficientHistory(row)" class="text-muted small"
                  [nbTooltip]="getInsufficientHistoryTooltip(row)">
                  Not enough prior history
                </span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </nb-card-body>
  </nb-card>
</div>
